<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
    <UiCard class="w-full max-w-md">
      <UiCardHeader class="space-y-1 text-center">
        <UiCardTitle>{{ $t('auth.verifyEmail.title') }}</UiCardTitle>
        <UiCardDescription>
          {{ isVerifying ? $t('auth.verifyEmail.verifying') : $t('auth.verifyEmail.description') }}
        </UiCardDescription>
      </UiCardHeader>
      <UiCardContent class="space-y-4">
        <!-- Loading State -->
        <div v-if="isVerifying" class="flex justify-center py-8">
          <Icon name="tabler:loader-2" size="48" class="animate-spin text-primary" />
        </div>

        <!-- Success Alert -->
        <UiAlert v-if="success" variant="success">
          <Icon name="tabler:circle-check" size="16" />
          <UiAlertDescription>{{ $t('auth.verifyEmail.successMessage') }}</UiAlertDescription>
        </UiAlert>

        <!-- Error Alert -->
        <UiAlert v-if="error" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ error }}</UiAlertDescription>
        </UiAlert>

        <!-- Invalid Token Alert -->
        <UiAlert v-if="!token && !isVerifying" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ $t('auth.verifyEmail.invalidToken') }}</UiAlertDescription>
        </UiAlert>

        <!-- Actions -->
        <div v-if="!isVerifying" class="space-y-3">
          <NuxtLink v-if="success" :to="$localeRoute('/account/login')">
            <UiButton class="w-full">
              {{ $t('auth.verifyEmail.continueToLogin') }}
            </UiButton>
          </NuxtLink>
          <NuxtLink v-else :to="$localeRoute('/')">
            <UiButton variant="outline" class="w-full">
              <Icon name="tabler:home" size="16" />
              {{ $t('auth.verifyEmail.backToHome') }}
            </UiButton>
          </NuxtLink>
        </div>
      </UiCardContent>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n, useRoute, useSeoMeta } from '#imports';
import { useAuthClient } from '#composables/useAuthClient';

const { $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.verifyEmail.seo.title') });

const authClient = useAuthClient();
const route = useRoute();

const token = computed(() => route.query.token as string | undefined);
const isVerifying = ref(true);
const error = ref<string | null>(null);
const success = ref(false);

onMounted(async () => {
  if (!token.value) {
    isVerifying.value = false;
    return;
  }

  try {
    const result = await authClient.verifyEmail({
      query: {
        token: token.value,
      },
    });

    if (result.error) {
      error.value = result.error.message || 'Email verification failed. The link may have expired.';
    } else {
      success.value = true;
    }
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'An unexpected error occurred during verification.';
  } finally {
    isVerifying.value = false;
  }
});
</script>
